import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteBlackoutPeriod } from "@/data/events";
import DropdownMenu from "@/components/General/DropdownMenu";
import { Query } from "@/utils/react-query/Queries";
import { QueryKeys } from "@/utils/react-query/QueryKeys";
import Unicon from "@/components/General/Unicon";
import { View, Text } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { showAlert } from "@/utils/showAlert";

const BlackoutPeriodItem = ({ blackoutPeriod }) => {
    const queryClient = useQueryClient();
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();

    const startDate = new Date(blackoutPeriod.startDate);
    const endDate = new Date(blackoutPeriod.endDate);

    const formatDateString = (date) => {
        const isMidnight = date.getHours() === 0 && date.getMinutes() === 0;
        const isEndOfDay = date.getHours() === 23 && date.getMinutes() === 59;

        if (isMidnight || isEndOfDay) {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        }

        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const startDateString = formatDateString(startDate);
    const endDateString = formatDateString(endDate);

    const dateRangeText = startDateString === endDateString
        ? startDateString
        : `${startDateString} – ${endDateString}`;

    const deleteBlackoutMutation = useMutation({
        mutationFn: deleteBlackoutPeriod,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.blackoutDates.all() });
        },
    })

    const removeBlackout = () => {
        deleteBlackoutMutation.mutate(blackoutPeriod.id);
    }

    const isSelf = blackoutPeriod.personID == loggedInUser?.id;
    const isFamilyMember = loggedInUser?.Family?.FamilyMembers?.some(m => m.id === blackoutPeriod.personID);
    const isAdmin = loggedInUser?.isAdmin;
    const canEdit = isSelf || isFamilyMember || isAdmin;

    const options = [];
    if (canEdit) {
        options.push({
            name: "Edit",
            icon: { web: "pencil-outline", ios: "pencil", android: "edit" },
            onSelect: () => router.push({
                pathname: '/events/blackouts/create',
                params: {
                    blackoutID: blackoutPeriod.id,
                    startDate: blackoutPeriod.startDate,
                    endDate: blackoutPeriod.endDate,
                }
            })
        });
        options.push({
            name: "Delete",
            destructive: true,
            icon: { web: "trash-outline", ios: "trash", android: "delete" },
            onSelect: () => showAlert({
                title: `Are you sure you want to remove this blackout period? ${isSelf ? 'You' : blackoutPeriod.Person?.firstName} will be shown as available on these dates.`,
                isDestructive: true,
                confirmText: "Delete",
                onConfirm: removeBlackout,
            })
        });
    }

    return (
        <View style={styles.card}>
            <View style={styles.content}>
                <View style={styles.iconWrap}>
                    <Unicon name="calendar-xmark" library="fa" size={20} colorVariant="error" />
                </View>

                <View style={styles.textBlock}>
                    <Text style={styles.name} numberOfLines={1}>
                        {blackoutPeriod.Person?.firstName} {blackoutPeriod.Person?.lastName}
                    </Text>
                    <View style={styles.dateRow}>
                        <Unicon name="time-outline" size={12} colorVariant="textTertiary" />
                        <Text style={styles.dateText}>{dateRangeText}</Text>
                    </View>
                </View>

                {options.length > 0 && (
                    <View style={styles.menuWrap}>
                        <DropdownMenu testID={`BlackoutPeriodMenu-${blackoutPeriod.id}`} options={options} />
                    </View>
                )}
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography, roundness }) => ({
    card: {
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.md,
        marginBottom: spacing.sm + 2,
        boxShadow: shadows.card,
        _web: {
            transition: "box-shadow 0.15s ease, transform 0.15s ease",
            _hover: { boxShadow: shadows.cardHover },
        },
    },
    content: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
    },
    iconWrap: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.errorContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    textBlock: {
        flex: 1,
        gap: spacing.xs,
    },
    name: {
        fontSize: typography.h3,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    dateRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
    },
    dateText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    menuWrap: {
        marginLeft: spacing.xs,
    },
}));

export default BlackoutPeriodItem
